// Sign-in and first-run setup.
//
// Registration is only offered when the app has no account at all — this is a
// personal app on a personal server, not a service anyone can join.

import { useState } from 'react';
import { api, type AuthState } from '../lib/api.ts';
import { Button, Card, ErrorNote, Field, TextInput } from '../components/ui.tsx';

export function SignIn({ state, onSignedIn }: { state: AuthState; onSignedIn: () => void }) {
  const setup = state.needs_setup;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (setup) await api.auth.setup({ name: name.trim(), email: email.trim(), password });
      else await api.auth.login({ email: email.trim(), password });
      onSignedIn();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-5">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-semibold">HealthLab</h1>
          <p className="text-sm text-muted mt-1">
            {setup ? 'Set up your private health lab.' : 'Sign in to continue.'}
          </p>
        </header>

        <Card as="div" className="space-y-4">
          <form
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); void submit(); }}
          >
            {setup && (
              <Field label="Your name" required>
                <TextInput value={name} onChange={setName} autoComplete="name" />
              </Field>
            )}

            <Field label="Email" required>
              <TextInput value={email} onChange={setEmail} type="email" inputMode="email"
                autoComplete="username" />
            </Field>

            <Field
              label="Password"
              required
              hint={setup ? 'At least 12 characters. A memorable phrase beats a short complicated one.' : undefined}
            >
              <TextInput value={password} onChange={setPassword} type="password"
                autoComplete={setup ? 'new-password' : 'current-password'} />
            </Field>

            {error && <ErrorNote message={error} />}

            <Button
              variant="primary"
              full
              type="submit"
              disabled={busy || !email.trim() || !password || (setup && !name.trim())}
            >
              {busy ? 'Working…' : setup ? 'Create my account' : 'Sign in'}
            </Button>
          </form>
        </Card>

        {setup && (
          <p className="text-xs text-muted text-center mt-4 leading-relaxed">
            Your health data stays on this server. It is never shared and no analytics are collected.
          </p>
        )}
      </div>
    </div>
  );
}
